import { useEffect, useState } from 'react'
import {
    Grid,
    Paper,
    Typography,
    Button,
    Chip,
    Box,
    Divider,
    Alert
} from '@mui/material'
import {
    ContentCopy as ContentCopyIcon,
    CheckCircle as CheckCircleIcon,
    Security as SecurityIcon,
    VpnKey as VpnKeyIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Badge as BadgeIcon
} from '@mui/icons-material'
import { useSitesContext, useUsersContext } from '../../context'

const PermissionUser = ({ width, height, typeDisplay, id, userData }) => {
    const { sites } = useSitesContext()
    const { roles } = useUsersContext()
    const [selectedSiteNames, setSelectedSiteNames] = useState([])
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (userData && userData.obras && userData.obras.length > 0 && sites && sites.length > 0) {
            const names = userData.obras.map((obraId) => {
                const idToFind = typeof obraId === 'object' ? obraId._id : obraId
                const siteFound = sites.find(s => s._id === idToFind)
                return siteFound ? siteFound.descripcion : null
            }).filter(name => name !== null)
            setSelectedSiteNames(names)
        } else {
            setSelectedSiteNames([])
        }
    }, [userData.obras, sites])

    // Find role object with full details & permissions
    const selectedRoleObject = typeof userData.role === 'object'
        ? userData.role
        : (roles || []).find(r => r._id === userData.role)

    const handleCopyCredentials = () => {
        const textToCopy = `CREDENCIALES DE USUARIO ICV\nUsar rut o correo para iniciar sesión\n-------------------------\nNombre: ${userData.name} ${userData.lastName}\nRUT: ${userData.rut}\nCorreo: ${userData.email}\nContraseña: ${userData.password || '(Sin cambios)'}`

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(textToCopy)
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = textToCopy
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            document.execCommand('copy')
            textArea.remove()
        }

        setCopied(true)
        setTimeout(() => setCopied(false), 4000)
    }

    return (
        <Grid container spacing={2} style={{ maxHeight: height || 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <Grid item xs={12}>
                <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 4, bgcolor: '#ffffff' }}>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                        Confirmación de Datos del Usuario
                    </Typography>

                    <Grid container spacing={3}>

                        {/* Column 1: Datos Personales & Obras */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: '#fafafa', height: '100%' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon color="primary" /> Datos del Usuario
                                </Typography>

                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>RUT</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{userData.rut}</Typography>
                                </Box>

                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Nombre y Apellido</Typography>
                                    <Typography variant="body1">{userData.name} {userData.lastName}</Typography>
                                </Box>

                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Correo electrónico</Typography>
                                    <Typography variant="body1">{userData.email || 'N/A'}</Typography>
                                </Box>

                                {userData.phone && (
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Teléfono</Typography>
                                        <Typography variant="body1">+56 {userData.phone}</Typography>
                                    </Box>
                                )}

                                <Divider sx={{ my: 1.5 }} />

                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon color="primary" /> Obras Asignadas ({selectedSiteNames.length}/3)
                                </Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selectedSiteNames.length > 0 ? (
                                        selectedSiteNames.map((name, index) => (
                                            <Chip key={index} label={name} color="success" variant="outlined" size="small" />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">Ninguna obra seleccionada</Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Column 2: Roles, Permisos & Credenciales Copiables */}
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>

                                {/* Role & Permissions Card */}
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: '#f4f6f9' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SecurityIcon color="primary" /> Rol y Permisos Enlazados
                                        </Typography>

                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            {userData.roleName || selectedRoleObject?.name || 'N/A'}
                                        </Typography>

                                        {selectedRoleObject?.description && (
                                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, fontStyle: 'italic' }}>
                                                {selectedRoleObject.description}
                                            </Typography>
                                        )}

                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                            Permisos asignados ({(selectedRoleObject?.permissions || []).length}):
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 110, overflowY: 'auto' }}>
                                            {(selectedRoleObject?.permissions || []).length > 0 ? (
                                                selectedRoleObject.permissions.map((perm, pIdx) => (
                                                    <Chip
                                                        key={perm._id || pIdx}
                                                        label={typeof perm === 'object' ? (perm.description || perm.key) : perm}
                                                        size="small"
                                                        color="primary"
                                                        variant="contained"
                                                        sx={{ fontSize: 11 }}
                                                    />
                                                ))
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">Sin permisos específicos asignados</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Credenciales Copiables Box */}
                                {typeDisplay === 'Nuevo usuario' && (
                                    <Grid item xs={12}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: '#e8f5e9',
                                                border: '2px solid #66bb6a'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <VpnKeyIcon /> Credenciales del Usuario
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                                    onClick={handleCopyCredentials}
                                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                                >
                                                    {copied ? '¡Copiado!' : 'Copiar Credenciales'}
                                                </Button>
                                            </Box>

                                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Rut</Typography>
                                                    <Paper variant="outlined" sx={{ p: 0.8, bgcolor: '#fff', fontSize: 13, wordBreak: 'break-all' }}>
                                                        {userData.rut || 'N/A'}
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Email</Typography>
                                                    <Paper variant="outlined" sx={{ p: 0.8, bgcolor: '#fff', fontSize: 13, wordBreak: 'break-all' }}>
                                                        {userData.email || 'N/A'}
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Contraseña</Typography>
                                                    <Paper variant="outlined" sx={{ p: 0.8, bgcolor: '#fff', fontSize: 13, fontWeight: 'bold', color: '#c62828' }}>
                                                        {userData.password || '••••••••'}
                                                    </Paper>
                                                </Grid>
                                            </Grid>

                                            {copied && (
                                                <Alert severity="success" sx={{ mt: 1.5, py: 0, px: 1 }}>
                                                    Credenciales copiadas al portapapeles.
                                                </Alert>
                                            )}
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    )
}

export default PermissionUser
